import { useEffect, useState } from "react";
import { CalendarRange, Loader2, RefreshCw } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import apiClient from "@/lib/api";
import {
  BOOKING_STATUS_LABELS, BOOKING_STATUS_STYLES, BOOKING_STATUS_FILTERS,
  DURATION_UNIT_LABELS, formatRupiah, formatDate,
} from "@/components/booking/bookingMeta";

export default function AdminBookingsPage() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = (status = filter) => {
    setLoading(true);
    setError(false);
    const qs = status && status !== "all" ? `?status=${status}` : "";
    apiClient
      .get(`/admin/bookings${qs}`)
      .then((r) => setBookings(r.data || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  return (
    <AdminLayout testid="admin-bookings-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Semua Booking</h1>
          <p className="mt-2 text-sm text-muted-foreground">Pantau seluruh transaksi booking di platform.</p>
        </div>
        <button onClick={() => load(filter)} data-testid="admin-bookings-refresh"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-primary border border-border hover:bg-secondary/60 transition-colors">
          <RefreshCw size={14} /> Muat Ulang
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="admin-bookings-filters">
        {BOOKING_STATUS_FILTERS.map((f) => (
          <button key={f.value} onClick={() => setFilter(f.value)}
            data-testid={`admin-bookings-filter-${f.value}`}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              filter === f.value ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary/60"
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-10 grid place-items-center py-16"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : error ? (
        <div className="mt-8 rounded-2xl border border-destructive/30 bg-destructive/10 p-8 text-center" data-testid="admin-bookings-error">
          <p className="text-sm text-destructive">Gagal memuat booking.</p>
          <button onClick={() => load(filter)} className="mt-3 text-sm font-semibold text-primary hover:underline">Coba lagi</button>
        </div>
      ) : bookings.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-secondary/30 p-12 text-center" data-testid="admin-bookings-empty">
          <span className="grid place-items-center w-14 h-14 rounded-2xl bg-secondary text-primary mx-auto mb-4"><CalendarRange size={26} /></span>
          <h3 className="font-heading text-lg font-medium text-foreground">Belum ada booking.</h3>
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-2xl border border-border" data-testid="admin-bookings-list">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Property</th>
                <th className="px-4 py-3">Kamar</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Periode</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b, i) => (
                <tr key={b.id} data-testid={`admin-booking-${i}`} className="border-t border-border">
                  <td className="px-4 py-3 font-medium">{b.property_name || "-"}</td>
                  <td className="px-4 py-3">{b.room_number}{b.room_name ? ` · ${b.room_name}` : ""}</td>
                  <td className="px-4 py-3">{b.customer_name || "-"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{formatDate(b.start_date)} → {formatDate(b.end_date)}<br /><span className="text-xs">{b.duration} {DURATION_UNIT_LABELS[b.duration_unit]}</span></td>
                  <td className="px-4 py-3 font-semibold text-primary">{formatRupiah(b.total_amount)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${BOOKING_STATUS_STYLES[b.status] || ""}`} data-testid={`admin-booking-status-${i}`}>
                      {BOOKING_STATUS_LABELS[b.status] || b.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
